import type { PostHog } from 'posthog-node'

import {
  AppCookie,
  CookieOptions,
  cookieOptions,
  expireCookieOptions
} from '../cookies'
import { getPartnerAppDomain } from '../env'
import { validateRedirectUrl } from '../security'

/**
 * Recovery for expired Hydra OAuth challenges (default TTL: 30 minutes).
 *
 * When Hydra rejects a challenge as expired (401) — or the auth app is opened
 * with no challenge at all — the browser is silently sent somewhere that
 * mints a brand-new OAuth flow instead of dead-ending on an error page. The
 * restart target is picked by a fallback ladder:
 *
 *   1. `client_origin` — the exact client app that initiated the dead flow
 *      (origin of the `redirect_uri` in the login request's `request_url`,
 *      remembered in a cookie while the challenge was still valid).
 *   2. `partner_cookie` — any prior valid visit pinned the partner in the
 *      `partner-id-auth` cookie (6-month TTL); restart at that partner's
 *      canonical payments portal.
 *   3. `partner_app_default` — the partner app, whose index auto-starts a
 *      fresh OAuth flow for logged-out visitors. Heals first-contact
 *      browsers holding a frozen sign-in link and no cookies at all.
 *
 * A counter cookie budgets automatic restarts per window so a broken flow
 * can never loop: re-clicking a frozen link heals transparently every time
 * (a restart that reaches a valid login form clears the counter), while a
 * no-user-input redirect loop exhausts the budget and stops on the error
 * page.
 *
 * Measurement uses a second, longer-lived cookie: the budget marker must be
 * cleared as soon as a valid form renders (so future expiries can restart),
 * but that made every credential-typing recovery invisible to analytics.
 * The pending-recovery cookie carries `<tier>:<surface>` until the restarted
 * flow completes a login, at which point `auth_flow_restart_recovered` fires
 * with the tier/surface of the restart that healed the user.
 */

// Structural subset of the `cookies` package instance held by the auth app's
// handlers — keeps this package free of the dependency and the helpers
// trivially testable.
export interface CookieStore {
  get: (name: string) => string | undefined
  set: (name: string, value?: string, options?: CookieOptions) => unknown
}

export const getOAuthClientOrigin = (
  requestUrl?: string | null
): string | null => {
  if (!requestUrl) {
    return null
  }

  try {
    const redirectUri = new URL(requestUrl).searchParams.get('redirect_uri')
    if (!redirectUri) {
      return null
    }

    return validateRedirectUrl(new URL(redirectUri).origin)
  } catch {
    return null
  }
}

export const persistOAuthClientOrigin = (
  cookies: CookieStore,
  requestUrl?: string | null
): void => {
  const origin = getOAuthClientOrigin(requestUrl)
  if (!origin) {
    return
  }

  cookies.set(
    AppCookie.OAuthClientOrigin,
    origin,
    cookieOptions(AppCookie.OAuthClientOrigin, { httpOnly: true })
  )
}

export type AuthFlowRestartTier =
  | 'client_origin'
  | 'partner_cookie'
  | 'partner_app_default'

export type AuthFlowRestartDecision =
  | { restartUrl: string; tier: AuthFlowRestartTier; blockedReason: null }
  | { restartUrl: null; tier: null; blockedReason: 'restart_budget_exhausted' }

export interface AuthFlowRestartOptions {
  /** Host of the current request — picks the env-correct partner app (tier 3) */
  host?: string | null
  /** Resolves a partner id to its canonical payments portal URL (tier 2) */
  resolvePartnerPortalUrl?: (
    partnerId: string
  ) => Promise<string | null | undefined>
}

// Automatic restarts allowed per marker window (cookie TTL: 5 minutes).
// Reaching a valid login form clears the marker, so only flows that keep
// dying without ever rendering a form can exhaust the budget.
export const AUTH_FLOW_RESTART_BUDGET = 3

const RESTART_TIERS: readonly string[] = [
  'client_origin',
  'partner_cookie',
  'partner_app_default'
]

// Marker cookie value is `<count>:<tier>`; pre-budget deploys stored '1'
const readAuthFlowRestartCount = (cookies: CookieStore): number => {
  const raw = cookies.get(AppCookie.AuthFlowRestarted)
  if (!raw) {
    return 0
  }

  const count = Number.parseInt(raw.split(':')[0], 10)

  // An unparsable value still counts as one spent restart rather than
  // resetting the guard
  return Number.isFinite(count) && count > 0 ? count : 1
}

// Portal URLs come from the gateway without a scheme
// (e.g. "acme.alternativepayments.io")
const normalizePortalUrl = (url?: string | null): string | undefined => {
  if (!url) {
    return undefined
  }

  return /^https?:\/\//.test(url) ? url : `https://${url}`
}

export const decideAuthFlowRestart = async (
  cookies: CookieStore,
  { host, resolvePartnerPortalUrl }: AuthFlowRestartOptions = {}
): Promise<AuthFlowRestartDecision> => {
  if (readAuthFlowRestartCount(cookies) >= AUTH_FLOW_RESTART_BUDGET) {
    return {
      restartUrl: null,
      tier: null,
      blockedReason: 'restart_budget_exhausted'
    }
  }

  // Tier 1 — re-validate on read: the cookie is client-held state
  const origin = validateRedirectUrl(cookies.get(AppCookie.OAuthClientOrigin))
  if (origin) {
    return { restartUrl: origin, tier: 'client_origin', blockedReason: null }
  }

  // Tier 2 — a resolver failure must not kill the ladder; fall through
  const partnerId = cookies.get(AppCookie.PartnerIdAuth)
  if (partnerId && resolvePartnerPortalUrl) {
    const portalUrl = await resolvePartnerPortalUrl(partnerId).catch(() => null)
    const validated = validateRedirectUrl(normalizePortalUrl(portalUrl))
    if (validated) {
      return {
        restartUrl: validated,
        tier: 'partner_cookie',
        blockedReason: null
      }
    }
  }

  // Tier 3 — the partner app's index SSR auto-starts a fresh OAuth flow for
  // logged-out visitors; `from` tags the redirect for measurement
  return {
    restartUrl: `${getPartnerAppDomain(host || '')}/?from=expired_login`,
    tier: 'partner_app_default',
    blockedReason: null
  }
}

export const markAuthFlowRestarted = (
  cookies: CookieStore,
  tier: AuthFlowRestartTier,
  surface?: string
): void => {
  cookies.set(
    AppCookie.AuthFlowRestarted,
    `${readAuthFlowRestartCount(cookies) + 1}:${tier}`,
    cookieOptions(AppCookie.AuthFlowRestarted, { httpOnly: true })
  )

  // Recovery attribution outlives the budget marker (see module doc)
  cookies.set(
    AppCookie.AuthFlowRestartPending,
    `${tier}:${surface ?? 'unknown'}`,
    cookieOptions(AppCookie.AuthFlowRestartPending, { httpOnly: true })
  )
}

export const wasAuthFlowRestarted = (cookies: CookieStore): boolean =>
  readAuthFlowRestartCount(cookies) > 0

export const clearAuthFlowRestartMarker = (cookies: CookieStore): void => {
  cookies.set(AppCookie.AuthFlowRestarted, '', expireCookieOptions())
}

export interface PendingAuthFlowRecovery {
  tier: AuthFlowRestartTier | null
  /** Surface that detected the expiry which triggered the restart */
  originSurface: string | null
}

/**
 * Restart awaiting a completed login. Non-null between a restart redirect
 * and either the login that heals it (fires `auth_flow_restart_recovered`)
 * or the cookie's 30-min expiry — it intentionally survives the budget
 * marker being cleared when a valid login form renders.
 */
export const getPendingAuthFlowRecovery = (
  cookies: CookieStore
): PendingAuthFlowRecovery | null => {
  const raw = cookies.get(AppCookie.AuthFlowRestartPending)
  if (!raw) {
    return null
  }

  const [rawTier, rawSurface] = raw.split(':')

  return {
    tier: RESTART_TIERS.includes(rawTier)
      ? (rawTier as AuthFlowRestartTier)
      : null,
    originSurface: rawSurface || null
  }
}

export const clearPendingAuthFlowRecovery = (cookies: CookieStore): void => {
  cookies.set(AppCookie.AuthFlowRestartPending, '', expireCookieOptions())
}

export const clearLoginChallengeCookie = (cookies: CookieStore): void => {
  cookies.set(AppCookie.LoginChallenge, '', expireCookieOptions())
}

/**
 * True when the tab that initiated the OAuth flow (`initiating_origin`, from
 * the `oauth-client-origin` cookie) and the client the challenge is actually
 * being accepted for (`redirectOrigin`, derived from the login request's
 * `request_url`) resolve to different hosts. This is the alertable signal for
 * the cross-app misroute: a concurrent tab from another app overwrote the
 * shared `login-challenge` cookie before this one accepted it. Either origin
 * being unknown is inconclusive, not a mismatch.
 */
export const computeOAuthClientMismatch = (
  initiatingOrigin: string | null | undefined,
  redirectOrigin: string | null | undefined
): boolean => {
  if (!initiatingOrigin || !redirectOrigin) {
    return false
  }

  try {
    return new URL(initiatingOrigin).host !== new URL(redirectOrigin).host
  } catch {
    return false
  }
}

export type AuthFlowEvent =
  | 'auth_login_challenge_expired'
  | 'auth_flow_restart_recovered'
  | 'auth_flow_restart_arrived'
  | 'auth_oauth_login_accepted'
  | 'auth_eks_redirect'
  | 'auth_eks_fallback'
  | 'auth_eks_refused'

export interface AuthFlowEventArgs {
  posthogInstance?: PostHog | null
  event: AuthFlowEvent
  distinctId?: string | null
  /**
   * Client session id (from the posthog-js cookie's `$sesid`) — stitches this
   * server event onto the same session timeline as the browser's events so
   * SSR/API telemetry can be correlated with what the user later renders.
   */
  sessionId?: string | null
  /**
   * Request user agent — server-captured events skip PostHog's client-side
   * bot detection, so this is the only property insights can filter
   * scanner/crawler traffic on
   */
  userAgent?: string | null
  /**
   * Pre-login events carry a real (anonymous) `distinct_id` for session
   * stitching but must not spawn a person profile. Set true to force
   * `$process_person_profile:false` even when a distinct id is present.
   */
  anonymous?: boolean
  properties?: Record<string, unknown>
}

// authFlowEventMessage is the PostHog message of one auth flow event
const authFlowEventMessage = ({
  event,
  distinctId,
  sessionId,
  userAgent,
  anonymous = false,
  properties = {}
}: AuthFlowEventArgs) => {
  const withSession = sessionId
    ? { ...properties, $session_id: sessionId }
    : properties

  const enriched = userAgent
    ? { ...withSession, $raw_user_agent: userAgent }
    : withSession

  // Attribute to a person only when we have an identity and this isn't a
  // pre-login event; otherwise count it without creating a junk person
  // profile per occurrence.
  const createPersonProfile = Boolean(distinctId) && !anonymous

  return {
    distinctId: distinctId || 'auth-app-server',
    event,
    properties: createPersonProfile
      ? enriched
      : { ...enriched, $process_person_profile: false }
  }
}

export const captureAuthFlowEvent = (args: AuthFlowEventArgs): void => {
  args.posthogInstance?.capture(authFlowEventMessage(args))
}

// The most a request waits for its event to reach PostHog
const SEND_TIMEOUT_MS = 500

/**
 * Sends the event and waits until PostHog has it, at most 500 ms. On Vercel a
 * function freezes once it answers, so an event only enqueued by
 * captureAuthFlowEvent before a redirect can arrive late or never. A request
 * that redirects right after its event awaits this one instead. It never
 * throws: a failed or slow send is dropped.
 */
export const sendAuthFlowEvent = async (
  args: AuthFlowEventArgs
): Promise<void> => {
  if (!args.posthogInstance) {
    return
  }
  let timer: ReturnType<typeof setTimeout> | undefined
  await Promise.race([
    args.posthogInstance
      .captureImmediate(authFlowEventMessage(args))
      .catch(() => undefined),
    new Promise<void>(resolve => {
      timer = setTimeout(resolve, SEND_TIMEOUT_MS)
    })
  ])
  clearTimeout(timer)
}

/**
 * Fires `auth_flow_restart_recovered` when a login completes while a pending
 * restart marker is set, attributing the heal to the ladder tier and origin
 * surface stored in the marker, then clears it. Returns whether a recovery
 * was counted.
 *
 * Every code path that finishes a login for a restarted flow must call this —
 * not just the /api/verification accept — or the recovery goes uncounted (the
 * skip-login accept on /login never reaches verification). `surface` names the
 * accept path doing the counting; `properties` carries any path-specific
 * extras (e.g. the login_challenge) merged into the event.
 */
export const captureAuthFlowRecoveryIfPending = ({
  posthogInstance,
  cookies,
  surface,
  distinctId,
  sessionId,
  userAgent,
  partnerId,
  properties = {}
}: {
  posthogInstance?: PostHog | null
  cookies: CookieStore
  surface: string
  distinctId?: string | null
  sessionId?: string | null
  userAgent?: string | null
  partnerId?: string | null
  properties?: Record<string, unknown>
}): boolean => {
  const pendingRecovery = getPendingAuthFlowRecovery(cookies)
  if (!pendingRecovery) {
    return false
  }

  captureAuthFlowEvent({
    posthogInstance,
    event: 'auth_flow_restart_recovered',
    distinctId,
    sessionId,
    userAgent,
    properties: {
      ...properties,
      surface,
      partner_id: partnerId,
      restart_tier: pendingRecovery.tier,
      origin_surface: pendingRecovery.originSurface
    }
  })
  clearPendingAuthFlowRecovery(cookies)

  return true
}
